import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipLink from "@/components/SkipLink";
import SectionHeader from "@/components/SectionHeader";
import CTAButton from "@/components/CTAButton";
import { StructuredData, SEO } from "@/components/SEO";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function NutritionPage() {
  const breadcrumbData = {
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://fiteasy.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Nutrition",
        item: "https://fiteasy.com/nutrition",
      },
    ],
  };

  const features = [
    "Comprehensive health & lifestyle assessment",
    "Personalized goal-based meal planning",
    "Weekly check-ins & real-time adjustments",
    "Seamless integration with your workout plans",
    "Macro and micronutrient tracking",
    "Recipe library with cultural preferences",
    "Grocery shopping lists",
    "Habit-building strategies",
  ];

  return (
    <>
      <SEO
        title="Nutrition"
        description="Work with FitEasy nutrition experts for personalized meal plans, health assessments, and ongoing support. Evidence-based nutrition tailored to your lifestyle and goals."
        path="/nutrition"
      />
      <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      <div className="bg-gradient-to-b from-slate-50 to-blue-50 text-gray-900 min-h-screen">
        <SkipLink />
        <Header />

        <main id="main-content" className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <section aria-labelledby="nutrition-heading" className="grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-center mb-12">
            <div className="space-y-4">
              <SectionHeader
                id="nutrition-heading"
                label="Nutrition"
            title="Science-backed nutrition coaching for lasting transformation"
            description="FitEasy connects you with registered nutritionists and dietitians who understand that nutrition is deeply personal. Our experts don't just count calories—they design comprehensive nutrition strategies that consider your lifestyle, cultural preferences, dietary restrictions, health conditions, and long-term goals. We believe sustainable results come from building healthy habits, not following restrictive diets."
                className="mb-0"
              />
              <ul className="mt-4 grid gap-3 text-sm text-gray-700 sm:grid-cols-2" role="list">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2" role="listitem">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <CTAButton
                  href="/programs"
                  variant="primary"
                  ariaLabel="Get started with nutrition coaching"
                >
                  Get Started
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </CTAButton>
              </div>
            </div>
            <div className="relative h-64 w-full overflow-hidden rounded-3xl border border-blue-100 bg-white/80">
              <img
                src="/hero/hero_nutrition.png"
                alt="FitEasy nutrition consultation - Personalized meal planning and dietary guidance"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </section>

          <section aria-labelledby="process-heading" className="grid gap-8 md:grid-cols-3">
            <SectionHeader
              id="process-heading"
              label="Our Process"
              title="How nutrition coaching works"
              className="md:col-span-3 mb-0"
            />
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                1. Comprehensive health assessment
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Your nutritionist conducts a detailed assessment covering your medical history, current health conditions, medications, food allergies and intolerances, dietary preferences (vegetarian, vegan, cultural), current eating patterns, lifestyle factors, activity level, and specific goals. This comprehensive understanding ensures your plan is safe, effective, and sustainable.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                2. Custom meal plan creation
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Receive a fully personalized meal plan that includes daily meal suggestions, portion sizes, macro breakdowns, recipe links, grocery shopping lists, meal prep tips, and snack ideas. Your plan adapts to your schedule, cooking skills, budget, and cultural preferences while ensuring you meet your nutritional needs and goals.
              </p>
            </article>
            <article className="rounded-3xl border border-blue-100 bg-white/90 p-6 transition hover:shadow-lg focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2">
              <h2 className="font-display text-lg text-gray-900">
                3. Continuous support & adjustments
              </h2>
              <p className="mt-2 text-sm text-gray-700 leading-relaxed">
                Your nutritionist provides ongoing support through weekly check-ins, progress reviews, plan adjustments based on your results and feedback, answers to nutrition questions, and motivation when you need it. As your body changes and goals evolve, your plan evolves with you to ensure continued progress.
              </p>
            </article>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

